<template>
  <div
    v-if="open"
    class="fixed z-[1260] rounded-2xl border border-[#ff009d49] bg-white/95 shadow-2xl p-4 sm:p-5 overflow-y-auto app-scroll"
    :style="menuStyle"
    @click.stop
  >
    <div class="flex items-center justify-between mb-4">
      <h5 class="text-sm font-semibold text-zinc-700">
        Text Settings
      </h5>
      <button
        type="button"
        class="rounded-md p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
        @click="$emit('close')"
      >
        <XMarkIcon class="w-4 h-4" />
      </button>
    </div>

    <div class="space-y-5">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Text Size
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="10"
            max="96"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedFontSize"
            @input="onFontSizeRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="10"
              max="96"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedFontSize"
              @input="onFontSizeNumberInput"
            >
            <span class="text-xs text-zinc-500">px</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Font Weight
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="100"
            max="900"
            step="100"
            class="w-full accent-[#ff009dce]"
            :value="normalizedFontWeight"
            @input="onFontWeightRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="100"
              max="900"
              step="100"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedFontWeight"
              @input="onFontWeightNumberInput"
            >
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Text Color
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="h-9 w-9 rounded-md border border-zinc-300 shadow-sm"
            :style="{ backgroundColor: normalizedTextColor }"
            @click="showColorPicker = !showColorPicker"
          />
          <input
            type="text"
            maxlength="7"
            class="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm text-zinc-700 uppercase focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
            :value="normalizedTextColor"
            @input="onColorTextInput"
            @blur="onColorTextBlur"
          >
          <button
            type="button"
            class="rounded-md border border-zinc-300 px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
            @click="showColorPicker = !showColorPicker"
          >
            HEX
          </button>
        </div>
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <div
            v-if="showColorPicker"
            class="relative mt-3"
          >
            <ModernColorPicker
              v-model="colorModel"
              class="z-50 relative"
            />
          </div>
        </transition>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Alignment
        </p>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="option in alignmentOptions"
            :key="option.value"
            type="button"
            class="rounded-md border px-2 py-1.5 text-sm transition-colors"
            :class="normalizedTextAlign === option.value
              ? 'border-[#ff009dce] bg-[#ff009d14] text-[#ff009dce]'
              : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'"
            @click="updateSettings({ textAlign: option.value })"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import ModernColorPicker from '@/Components/ModernColorPicker.vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  menuStyle: {
    type: Object,
    default: () => ({ top: '0px', left: '0px', width: '320px', height: '360px' }),
  },
  settings: {
    type: Object,
    default: () => ({
      fontSize: 16,
      fontWeight: 400,
      textColor: '#18181b',
      textAlign: 'center',
    }),
  },
});

const emit = defineEmits(['update:settings', 'close']);

const clampFontSize = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 16;
  return Math.min(96, Math.max(10, Math.round(num)));
};
const clampFontWeight = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 400;
  return Math.min(900, Math.max(100, Math.round(num / 100) * 100));
};

const normalizeColor = (value) => {
  const raw = String(value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase();
  return '#18181B';
};

const normalizeAlign = (value) => {
  const v = String(value || '').toLowerCase();
  if (v === 'left' || v === 'center' || v === 'right') return v;
  return 'center';
};

const normalizedFontSize = computed(() => clampFontSize(props.settings?.fontSize));
const normalizedFontWeight = computed(() => clampFontWeight(props.settings?.fontWeight));
const normalizedTextColor = computed(() => normalizeColor(props.settings?.textColor));
const normalizedTextAlign = computed(() => normalizeAlign(props.settings?.textAlign));
const showColorPicker = ref(false);
const colorModel = computed({
  get: () => normalizedTextColor.value,
  set: (value) => {
    updateSettings({ textColor: value });
  },
});

const alignmentOptions = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const updateSettings = (patch = {}) => {
  emit('update:settings', {
    fontSize: clampFontSize(patch.fontSize ?? normalizedFontSize.value),
    fontWeight: clampFontWeight(patch.fontWeight ?? normalizedFontWeight.value),
    textColor: normalizeColor(patch.textColor ?? normalizedTextColor.value),
    textAlign: normalizeAlign(patch.textAlign ?? normalizedTextAlign.value),
  });
};

const onFontSizeRangeInput = (event) => {
  updateSettings({ fontSize: event?.target?.value });
};

const onFontSizeNumberInput = (event) => {
  updateSettings({ fontSize: event?.target?.value });
};
const onFontWeightRangeInput = (event) => {
  updateSettings({ fontWeight: event?.target?.value });
};
const onFontWeightNumberInput = (event) => {
  updateSettings({ fontWeight: event?.target?.value });
};

const onColorTextInput = (event) => {
  const raw = String(event?.target?.value || '').trim();
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{3,6}$/.test(withHash)) {
    updateSettings({ textColor: withHash });
  }
};

const onColorTextBlur = (event) => {
  updateSettings({ textColor: event?.target?.value });
};
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
